import React, { useEffect, useState, useMemo } from 'react';
import { api, getProductsCatalog } from '../apiClient';
import Session from 'supertokens-auth-react/recipe/session';
import { useNavigate, useLocation } from 'react-router-dom';

const getAvailabilityStatus = (statuses) => {
  if (!statuses || statuses.length === 0) {
    return <span className="text-yellow-500">Unknown</span>;
  }

  const allInStock = statuses.every(s => s === 'in_stock');
  if (allInStock) {
    return <span className="text-green-500">In Stock</span>;
  }

  const allOutOfStock = statuses.every(s => s === 'out_of_stock');
  if (allOutOfStock) {
    return <span className="text-red-500">Out of Stock</span>;
  }

  return <span className="text-orange-500">Mixed Stock</span>;
};

const ProductCatalogPage = () => {
  const location = useLocation();
  const { selectedImage } = location.state || {};
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductType, setSelectedProductType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProductsCatalog();
        setProductsCatalog(data);
      } catch (err) {
        console.error('Error fetching products catalog:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const productTypes = useMemo(() => {
    const types = new Set();
    productsCatalog.forEach(product => {
      if (typeof product.name !== 'string') return;
      const match = product.name.match(new RegExp("(T-Shirt|Hoodie|Sweatshirt|Tank Top|Mug|Poster|Hat|Bag)", "i"));
      if (match) {
        types.add(match[0]);
      } else {
        types.add('Other');
      }
    });
    return ['All', ...Array.from(types).sort()];
  }, [productsCatalog]);

  const filteredProducts = useMemo(() => {
    let filtered = productsCatalog;

    if (selectedProductType && selectedProductType !== 'All') {
      filtered = filtered.filter(product => {
        if (typeof product.name !== 'string') return false;
        const match = product.name.match(new RegExp("(T-Shirt|Hoodie|Sweatshirt|Tank Top|Mug|Poster|Hat|Bag)", "i"));
        const type = match ? match[0] : 'Other';
        return type === selectedProductType;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [productsCatalog, searchTerm, selectedProductType]);

  const handleProductSelect = async (product) => {
    const sessionExists = await Session.doesSessionExist();
    if (!sessionExists) {
      navigate('/auth');
      return;
    }

    if (selectedImage) {
      navigate('/configure-mockup', { state: { selectedProduct: product, selectedImage: selectedImage } });
    } else {
      navigate('/gallery', { state: { selectedProduct: product } });
    }
  };

  return (
    <div className="container mx-auto p-4 bg-background rounded-lg shadow-lg tour-products">
      <h1 className="text-3xl font-bold mb-6 text-center">Product Catalog</h1>

      <div className="mb-6 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
        <input
          type="text"
          placeholder="Search products..."
          className="p-3 border border-gray-300 rounded-lg w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="p-3 border border-gray-300 rounded-lg w-full sm:w-1/4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
          value={selectedProductType}
          onChange={(e) => setSelectedProductType(e.target.value)}
        >
          {productTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-center text-gray-600">Loading products...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : filteredProducts.length === 0 ? (
        <p className="text-center text-gray-600">No products found matching your criteria.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 tour-step-9">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className="border border-gray-200 rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 tour-step-10"
              onClick={() => handleProductSelect(product)}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-64 object-contain"
              />
              <div className="p-4">
                <h2 className="text-lg font-semibold mb-1 text-gray-800">{product.name}</h2>
                <p className="text-gray-600 text-sm">Estimated Price: ${product.selling_price ? product.selling_price.toFixed(2) : 'N/A'}</p>
                <p className="text-xs text-gray-500 mt-2">Ships to: {product.coverage_locations && product.coverage_locations.length > 0 ? product.coverage_locations.slice(0, 3).join(', ') + (product.coverage_locations.length > 3 ? '...' : '') : 'Worldwide'}</p>
                <p className="text-xs text-gray-500 mt-1">Fulfillment: {product.avg_fulfillment_time ? `${product.avg_fulfillment_time} business days` : '5-7 business days'}</p>
                <p className="text-xs text-gray-500 mt-1">Availability: {getAvailabilityStatus(product.availability_statuses)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCatalogPage;
